import axios from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import styles from '../../css/admin/Footer.module.css';
import { useLanguage } from '../../hooks/LanguageContext';
import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';
import { userLogout } from '../../reducers/userInfo';
import translations from '../admin/translations';
import { isApp } from '../../utils/isApp'; // [APP-FORK]
export default function Footer() {
  if (isApp()) return null; // [APP-FORK]
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [pathname, setPathname] = useState('/');
  const params = useParams();
  const [store, setStore] = useState(() => ({}));
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const { fetchData } = useAxiosAdminFetch(); // useAxiosAdminFetch 사용

  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  console.log(
    'Footer render - current path:',
    pathname,
    'storeNo:',
    params.storeNo
  );
  // storeNo 추출 로직 (Header와 동일 키 지원)
  const segments = location.pathname.split('/').filter(Boolean);
  const getNextOf = key => {
    const idx = segments.indexOf(key);
    return idx !== -1 && segments[idx + 1] ? segments[idx + 1] : null;
  };
  const queryStoreNo = new URLSearchParams(location.search).get('storeNo');
  const extractedStoreNo =
    getNextOf('login') ||
    getNextOf('giftstatus') ||
    getNextOf('verification') ||
    getNextOf('verifyconfirm') ||
    getNextOf('verifysuccess') ||
    getNextOf('verifyfailure') ||
    getNextOf('status');
  const storeNo =
    params.storeNo ||
    queryStoreNo ||
    extractedStoreNo ||
    localStorage.getItem('last_storeNo') ||
    '';

  // 상점용 약관/개인정보 페이지는 shopuser 앱으로 이동
  // - 환경변수(REACT_APP_SHOPUSER_SERVER)를 베이스로 사용 (예: http://localhost:3001)
  const shopUserBase = (() => {
    const raw = process.env.REACT_APP_SHOPUSER_SERVER || '';
    if (!raw) return '';
    return raw.endsWith('/') ? raw : `${raw}/`;
  })();

  const fetchStoreInfo = async () => {
    if (!storeNo) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVER_PUBLIC}store/character/${storeNo}`
      );
      const data = response?.data?.data || {};
      setStore(data);
      localStorage.setItem(`store_${storeNo}`, JSON.stringify(data));
      localStorage.setItem('last_storeNo', String(storeNo));
      // 다른 컴포넌트에 캐시 갱신 알림
      window.dispatchEvent(new Event('storeDataUpdated'));
    } catch (error) {
      console.error('Error fetching store information:', error);
    }
  };

  useEffect(() => {
    setPathname(location.pathname);
  }, [location]);

  /**
   * 세션 상태 확인
   * - useAxiosAdminFetch를 사용하여 리프레시 처리 포함
   * - 액세스 토큰 만료 시 → 리프레시 시도 → 성공하면 페이지 그대로 유지
   * - 리프레시 토큰도 만료 시 → useAxiosAdminFetch에서 로그인 페이지로 이동
   */
  const checkSession = async () => {
    // 로그인 페이지에서는 세션 체크 안 함
    // verifysuccess 는 앱 전용(AppOnlyGuard) — 웹에선 401 이라 세션 체크를 건너뛴다.
    if (pathname.includes('/login') || pathname.includes('/verifysuccess')) {
      setIsCheckingSession(false);
      return;
    }

    setIsCheckingSession(true);
    try {
      // useAxiosAdminFetch 사용 → 401 시 자동 리프레시 처리
      const response = await fetchData({
        url: `${process.env.REACT_APP_API_SERVER}profile`,
        method: 'get',
      });

      // 성공하면 로그인 상태
      if (response?.status === 200) {
        setIsLoggedIn(true);
      } else {
        // 401이면 useAxiosAdminFetch에서 리프레시 처리됨
        // 리프레시 성공하면 다시 profile 호출됨 → 여기까지 도달하면 실패한 것
        setIsLoggedIn(false);
        // 로그인 페이지로 이동은 useAxiosAdminFetch에서 처리하므로 여기서 안 함
      }
    } catch (error) {
      // useAxiosAdminFetch에서 리프레시 처리하므로 여기서는 상태만 업데이트
      setIsLoggedIn(false);
    } finally {
      setIsCheckingSession(false);
    }
  };

  // storeNo가 바뀌면 캐시 먼저 반영 후 필요 시 API 조회
  useEffect(() => {
    if (!storeNo) return;
    const storedData = localStorage.getItem(`store_${storeNo}`);
    if (storedData) {
      try {
        setStore(JSON.parse(storedData));
      } catch (_) {}
    }
    fetchStoreInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeNo]);

  // 모바일 어드민 경로일 때 세션 상태 확인
  useEffect(() => {
    if (pathname.includes('/admin') || pathname.includes('/mobile')) {
      checkSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleLogout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_SERVER}logout`,
        {},
        { withCredentials: true }
      );
    } catch (e) {
      // ignore
    } finally {
      dispatch(userLogout());
      localStorage.removeItem('useremail');
      localStorage.removeItem('nickname');
      localStorage.removeItem('admin_userid');
      localStorage.removeItem('admin_password');
      const loginPath = storeNo
        ? `/mobile/login/${storeNo}/normal`
        : `/mobile/login`;
      navigate(loginPath);
    }
  };
  const handleAdminLogin = () => {
    const loginPath = storeNo
      ? `/mobile/login/${storeNo}/normal`
      : `/mobile/login`;
    navigate(loginPath);
  };

  const handleAdminLogout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_SERVER}logout`,
        {},
        { withCredentials: true }
      );
    } catch (e) {
      // ignore
    } finally {
      setIsLoggedIn(false);
      dispatch(userLogout());
      localStorage.removeItem('admin_userid');
      localStorage.removeItem('admin_password');
      Swal.fire({
        icon: 'success',
        title: t.adminLogout,
      }).then(() => {
        const loginPath = storeNo
          ? `/mobile/login/${storeNo}/normal`
          : `/mobile/login`;
        navigate(loginPath);
      });
    }
  };

  // 페이지에 따라 다른 푸터 렌더링
  const renderFooter = () => {
    if (pathname === '/checkout') {
      return;
    } else if (pathname.includes('/admin') || pathname.includes('/mobile')) {
      // 모바일 어드민 경로: 세션 상태에 따라 로그인/로그아웃 표시
      return (
        <div className={styles.footer}>
          <div className={styles.frame1}>
            <div className={styles.container}>
              {!isCheckingSession && (
                <div
                  className={styles.subcontainerText3}
                  onClick={isLoggedIn ? handleAdminLogout : handleAdminLogin}
                >
                  {isLoggedIn ? t.adminLogout : t.adminLogin}
                </div>
              )}
              <div className={styles.containerText}>
                {t.bizNumLabel} : {store?.bizNum ?? '849-85-02924'} |{' '}
                {t.representativeLabel} : {store?.adminName ?? '고승곤'}
              </div>
              <div className={styles.containerText}>
                {t.addressLabel} :{' '}
                {[store?.addr, store?.detAddr]
                  .filter(Boolean)
                  .join(' ')
                  .trim() ||
                  '인천 미추홀구 석정로 229, 811호 (도화동,인천블록체인허브센터)'}
              </div>
              <div className={styles.containerText}>
                {t.storeNameLabel} : {store?.storeName ?? '(주)오스리움'}
              </div>
              <div className={styles.containerText}>
                {t.operationHoursLabel}:{' '}
                {store?.operationHours ?? '평일 09:00 - 18:00'}
              </div>
              <div className={styles.containerText}>
                {store?.operationHoursNote ??
                  '(점심시간 12:00~13:00 미운영 / 주말 및 공휴일 휴무)'}
              </div>
              <div className={styles.subcontainer}>
                <div className={styles.subcontainerText1}>
                  {t.phoneLabel} : {store?.phone ?? '070-4010-8203'}
                </div>
              </div>
              <div className={styles.subcontainer}>
                <a
                  className={styles.subcontainerText3}
                  href={
                    storeNo
                      ? `${shopUserBase}service-terms?storeNo=${storeNo}`
                      : `${shopUserBase}service-terms`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.serviceTerms}
                </a>
                <div className={styles.subcontainerText2}>|</div>
                <a
                  className={styles.subcontainerText3}
                  href={
                    storeNo
                      ? `${shopUserBase}privacy?storeNo=${storeNo}`
                      : `${shopUserBase}privacy`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.privacyPolicy}
                </a>
              </div>
            </div>
          </div>
          <div className={styles.frame2}>
            {store?.storeName
              ? `Copyright by ${store.storeName}. All Rights Reserved`
              : t.poweredBy}
          </div>
        </div>
      );
    } else {
      // 일반 경로: 사용자 로그아웃 표시
      return (
        <div className={styles.footer}>
          <div className={styles.frame1}>
            <div className={styles.container}>
              <div className={styles.subcontainerText3} onClick={handleLogout}>
                {t.userLogout}
              </div>
              <div className={styles.containerText}>{store?.addr}</div>
              <div className={styles.subcontainer}>
                <div className={styles.subcontainerText1}>{store?.phone} </div>
                <div className={styles.subcontainerText2}>|</div>
                <div className={styles.subcontainerText3}>{t.email}</div>
              </div>
            </div>
            <div className={styles.button}>
              <div className={styles.buttonText}>
                {t.giftHome} {store?.qrValue}
              </div>
            </div>
          </div>
          <div className={styles.frame2}>
            {store?.storeName
              ? `Copyright by ${store.storeName}. All Rights Reserved`
              : t.poweredBy}
          </div>
        </div>
      );
    }
  };

  return renderFooter();
}
